<template>
    <div>
        <a v-if="backBtnVisible && !onboarding && !lead" class="sign-up-back-btn" @click="$emit('back')">
            <svg-icon name="keyboard_arrow_left" />
        </a>

        <form
            class="sign-up-form form"
            :class="{ 'sign-up-form--interest': !featureEnabled('ownerAccess') && !featureEnabled('renterAccess') }"
            @submit.prevent="submit"
        >
            <div v-if="!role" class="radio-list sign-up-form__role">
                <label class="radio-label renter-radio-label">
                    <input v-model="formData.role" type="radio" class="radio" value="renter" />
                    {{ $t('modals.signup.role_label_renter') }}
                </label>
                <label class="radio-label">
                    <input v-model="formData.role" type="radio" class="radio" value="owner" />
                    {{ $t('modals.signup.role_label_owner') }}
                </label>
            </div>
            <RenterAccessSignupForm
                v-if="formData.role === 'renter' && !featureEnabled('renterAccess')"
                :preset-email="formData.email"
            />
            <OwnerAccessSignupForm
                v-else-if="formData.role === 'owner' && !featureEnabled('ownerAccess')"
                :preset-email="formData.email"
            />
            <template v-else-if="featureEnabled('ownerAccess') || featureEnabled('renterAccess')">
                <div class="form__row">
                    <label class="label" for="email">
                        {{ $t('form_inputs.email_address_label') }}
                    </label>
                    <input
                        id="email"
                        ref="email"
                        v-model.trim="formData.email"
                        class="input"
                        :disabled="emailDisabled"
                        type="email"
                        :class="{
                            'input--error': $v.formData.email.$invalid && $v.formData.email.$dirty,
                        }"
                    />
                    <template v-if="$v.formData.email.$dirty">
                        <InputError v-if="!$v.formData.email.required" static-position>
                            {{ $t('errors.field_required') }}
                        </InputError>
                        <InputError v-if="!$v.formData.email.email" static-position>
                            {{ $t('errors.field_invalid') }}
                        </InputError>
                    </template>
                </div>
                <div v-if="formData.role === 'owner'" class="form__row">
                    <label class="checkbox-label">
                        <input v-model="formData.isOrganizationRepresentative" type="checkbox" class="checkbox" />
                        <span class="checkbox__text">
                            {{ $t('modals.signup.company_owner_checkbox') }}
                        </span>
                    </label>
                </div>
                <template v-if="formData.isOrganizationRepresentative">
                    <div class="form__row">
                        <label class="label" for="organizationName">
                            {{ $t('form_inputs.company_name_label') }}
                        </label>
                        <input
                            id="organizationName"
                            v-model.trim="formData.organizationName"
                            class="input"
                            type="text"
                            :class="{
                                'input--error':
                                    $v.formData.organizationName.$invalid && $v.formData.organizationName.$dirty,
                            }"
                        />
                        <InputError
                            v-if="!$v.formData.organizationName.required && $v.formData.organizationName.$dirty"
                            static-position
                        >
                            {{ $t('errors.field_required') }}
                        </InputError>
                    </div>
                    <div class="form__row">
                        <label class="label" for="organizationNumber">
                            {{ $t('form_inputs.organization_number_label') }}
                        </label>
                        <input
                            id="organizationNumber"
                            v-model.trim="formData.organizationNumber"
                            class="input"
                            type="text"
                            :class="{
                                'input--error':
                                    $v.formData.organizationNumber.$invalid && $v.formData.organizationNumber.$dirty,
                            }"
                        />
                        <InputError
                            v-if="!$v.formData.organizationNumber.required && $v.formData.organizationNumber.$dirty"
                            static-position
                        >
                            {{ $t('errors.field_required') }}
                        </InputError>
                    </div>
                </template>
                <div class="form__row">
                    <label class="label" for="firstName">
                        {{ $t('form_inputs.first_name_label') }}
                    </label>
                    <input
                        id="firstName"
                        ref="firstName"
                        v-model.trim="formData.firstName"
                        class="input"
                        type="text"
                        :class="{
                            'input--error': $v.formData.firstName.$invalid && $v.formData.firstName.$dirty,
                        }"
                    />
                    <InputError v-if="!$v.formData.firstName.required && $v.formData.firstName.$dirty" static-position>
                        {{ $t('errors.field_required') }}
                    </InputError>
                </div>
                <div class="form__row">
                    <label class="label" for="lastName">
                        {{ $t('form_inputs.last_name_label') }}
                    </label>
                    <input
                        id="lastName"
                        ref="lastName"
                        v-model.trim="formData.lastName"
                        class="input"
                        type="text"
                        :class="{
                            'input--error': $v.formData.lastName.$invalid && $v.formData.lastName.$dirty,
                        }"
                    />
                    <InputError v-if="!$v.formData.lastName.required && $v.formData.lastName.$dirty" static-position>
                        {{ $t('errors.field_required') }}
                    </InputError>
                </div>
                <template v-if="!onboarding">
                    <div class="form__row">
                        <label class="label" for="password"> {{ $t('form_inputs.password_label') }}</label>
                        <PasswordInput
                            v-model.trim="$v.formData.password.$model"
                            show-hints
                            :has-error="$v.formData.password.$invalid && $v.formData.password.$dirty"
                            :is-dirty="$v.formData.password.$dirty"
                        />
                    </div>
                </template>

                <div v-if="formData.role === 'owner'" class="form__row campaign-code">
                    <a
                        v-if="!displayCampaignCodeInput"
                        class="campaign-code-link"
                        href="#"
                        @click="displayCampaignCodeInput = true"
                    >
                        {{ $t('modals.signup.add_campaign_code') }}
                    </a>
                    <template v-if="displayCampaignCodeInput">
                        <label class="label" for="campaignCode" @click="displayCampaignCodeInput = false">
                            {{ $t('modals.signup.campaign_code_label') }}
                        </label>
                        <input
                            id="campaignCode"
                            v-model.trim="$v.formData.campaignCode.$model"
                            class="input"
                            type="text"
                        />
                    </template>
                </div>
                <div class="form__row">
                    <label class="checkbox-label">
                        <input v-model="formData.subscribeToNewsletter" type="checkbox" class="checkbox" />
                        <span class="checkbox__text">
                            {{ $t('modals.signup.subscribe_to_newsletter_label') }}
                        </span>
                    </label>
                </div>

                <div class="form__row">
                    <i18n tag="p" path="modals.signup.accept_terms_label" class="sign-up-form__terms">
                        <LocalizedLink :to="{ name: 'user-terms' }" target="_blank">
                            {{ $t('modals.signup.user_terms_link_text') }}
                        </LocalizedLink>
                        <LocalizedLink :to="{ name: 'privacy-policy' }" target="_blank">
                            {{ $t('modals.signup.privacy_policy_link_text') }}
                        </LocalizedLink>
                    </i18n>
                    <Notice v-if="isNameInvalid" class="sign-up-form__notice" type="warning">
                        {{ $t('errors.invalid_name_characters') }}
                    </Notice>
                    <Notice v-else-if="signUpError" class="sign-up-form__notice" type="warning">
                        <template v-if="hasValidationError('email')">
                            {{ $t('errors.email_already_registered') }}
                            <ModalLink to="login">{{ $t('modals.signup.login_link') }}</ModalLink>
                        </template>
                        <template v-else-if="hasValidationError('campaignCode')">
                            {{ $t('modals.signup.campaign_code_error') }}
                        </template>
                        <template v-else>
                            {{ $t('errors.signup_failed') }}
                            {{ $t('errors.try_again_or_contact_us') }}
                        </template>
                    </Notice>
                    <Notice v-if="displaySubscribeSuccess" class="sign-up-form__notice" type="success">
                        {{ $t('modals.signup.thank_you_notice') }}
                    </Notice>
                    <button
                        v-if="signupBtnVisible"
                        :disabled="isSaving"
                        class="button button--primary button--full-width"
                    >
                        <LoadingIndicator v-if="isSaving" />
                        <template v-else-if="buttonType === 'continue'">
                            {{ $t('buttons.continue') }}
                            <svg-icon name="keyboard_arrow_right" class="button__icon" />
                        </template>
                        <template v-else>
                            {{ $t('modals.signup.sign_up_btn') }}
                        </template>
                    </button>
                    <input v-else type="submit" hidden />
                </div>
            </template>
        </form>
    </div>
</template>

<script>
import { validationMixin } from 'vuelidate';
import { required, requiredIf, email, minLength } from 'vuelidate/lib/validators';

import { mapGetters } from 'vuex';
import { ORGANISATION_NUMBER_MIN_LENGTH } from '~/services/AuthService';
import Notice from '~/components/Notice.vue';
import LoadingIndicator from '~/components/LoadingIndicator.vue';
import RenterAccessSignupForm from '~/components/onboarding/RenterAccessSignupForm.vue';
import ModalLink from '~/components/modals/ModalLink.vue';
import OwnerAccessSignupForm from '~/components/onboarding/OwnerAccessSignupForm.vue';
import PasswordInput from '~/components/onboarding/PasswordInput.vue';
import InputError from '~/components/form-elements/InputError.vue';
import { actionTypes as userActions } from '~/store/user';

export default {
    components: {
        Notice,
        LoadingIndicator,
        RenterAccessSignupForm,
        ModalLink,
        OwnerAccessSignupForm,
        PasswordInput,
        InputError,
    },
    mixins: [validationMixin],
    validations() {
        const validations = {
            formData: {
                role: { required },
                firstName: {
                    required,
                    isValidName: this.$validators.isValidName,
                },
                lastName: {
                    required,
                    isValidName: this.$validators.isValidName,
                },
                email: {
                    required,
                    email,
                },
                organizationName: {
                    required: requiredIf(() => {
                        return this.formData.isOrganizationRepresentative;
                    }),
                },
                organizationNumber: {
                    required: requiredIf(() => {
                        return this.formData.isOrganizationRepresentative;
                    }),
                    minLengthValue: minLength(ORGANISATION_NUMBER_MIN_LENGTH),
                },
                campaignCode: {},
            },
        };

        if (!this.onboarding) {
            validations.formData.password = {
                required,
                isValidPassword: this.$validators.isValidPassword,
            };
        }

        return validations;
    },
    props: {
        role: {
            type: String,
            default: null,
        },
        email: {
            type: String,
            default: null,
        },
        onboarding: {
            type: Object,
            default: null,
        },
        backBtnVisible: {
            type: Boolean,
            default: true,
        },
        signupBtnVisible: {
            type: Boolean,
            default: true,
        },
        // 'create-account' or 'continue'
        buttonType: {
            type: String,
            default: 'create-account',
        },
        focusInput: {
            type: String,
            default: null,
        },
    },
    data() {
        let formData = {};

        if (this.onboarding) {
            formData = {
                onboardingId: this.onboarding.onboardingId,
                firstName: this.onboarding.firstName,
                lastName: this.onboarding.lastName,
                email: this.onboarding.email,
                isOrganizationRepresentative: this.onboarding.isOrganizationRepresentative || false,
                organizationName: this.onboarding.organizationName || null,
                organizationNumber: this.onboarding.organizationName || null,
            };
        } else if (this.$store.getters.lead) {
            const { lead } = this.$store.getters;

            formData = {
                firstName: lead.firstName || null,
                lastName: lead.lastName || null,
                email: lead.email || null,
                password: null,
                isOrganizationRepresentative: lead.isOrganizationRepresentative || false,
                organizationName: lead.organizationName || null,
                organizationNumber: lead.organizationName || null,
            };
        } else {
            formData = {
                firstName: null,
                lastName: null,
                email: null,
                password: null,
                isOrganizationRepresentative: false,
                organizationName: null,
                organizationNumber: null,
            };
        }

        if (this.role) {
            formData.role = this.role;
        } else {
            switch (this.$route.name) {
                case 'rent-out':
                    formData.role = 'owner';
                    break;
                case 'vehicle':
                    formData.role = 'renter';
                    break;
                default:
                // Don't preselect role for other pages
            }
        }

        if (this.email) {
            formData.email = this.email;
        }

        return {
            formData,
            isSaving: false,
            signUpError: null,
            displaySubscribeSuccess: false,
            displayCampaignCodeInput: false,
            isNameInvalid: false,
        };
    },
    computed: {
        ...mapGetters(['lead']),
        emailDisabled() {
            return this.onboarding?.email;
        },
    },
    mounted() {
        if (this.focusInput) {
            this.$nextTick(() => {
                this.$refs[this.focusInput]?.focus();
            });
        }
    },
    beforeDestroy() {
        this.$store.dispatch(userActions.SET_AUTH_DATA, null);
    },
    methods: {
        setIsSaving(value) {
            this.isSaving = value;
            this.$emit('is-saving', value);
        },
        hasValidationError(field) {
            if (!this.signUpError || this.signUpError.slug !== 'validation') {
                return false;
            }

            return typeof this.signUpError.errors[field] !== 'undefined';
        },
        async submit() {
            this.$v.formData.$touch();
            this.isNameInvalid = !this.$v.formData.firstName.isValidName || !this.$v.formData.lastName.isValidName;

            if (this.$v.formData.$invalid) {
                return;
            }

            this.setIsSaving(true);
            this.signUpError = null;

            try {
                const signupData = { ...this.formData, acceptsTerms: true };
                const response = await this.$authService.signUp(signupData);

                this.handleSignUpSuccess(response, signupData);
            } catch (error) {
                this.handleSignUpError(error);
            }

            this.setIsSaving(false);
        },
        async handleSignUpSuccess(response, signupData) {
            this.dispatchTrackingEvent(response);

            if (!/(sign-up)|(renter-signup)/.test(this.$route.name)) {
                sessionStorage.setItem('initial-path', this.$route.fullPath);
            }

            if (!this.$auth.loggedIn) {
                await this.$authService.login({
                    email: signupData.email,
                    password: signupData.password,
                });
            }

            this.$emit('signed-up', response);
        },
        handleSignUpError(error) {
            this.signUpError = error;
            this.$sentry.captureException(error);
        },
        dispatchTrackingEvent(signupData) {
            let userType = 'Renter';

            if (signupData.role === 'owner') {
                userType = 'Owner';

                if (signupData.isOrganizationRepresentative) {
                    userType = 'Company owner';
                }
            }

            this.$gtm.trackEvent({
                category: 'Onboarding',
                action: 'Create account',
                label: userType,
                email: signupData.email,
                phone: signupData.phone,
                user_id: signupData.id,
            });
        },
    },
};
</script>

<style lang="scss">
@import '@/sass/_variables.scss';

.sign-up-form {
    .renter-radio-label.radio-label--disabled {
        text-decoration: line-through;
    }

    &__role {
        margin-bottom: 20px;
    }

    &__notice,
    &__terms {
        margin-bottom: 16px;
    }

    &__terms {
        @include font-size(14px);
    }

    .campaign-code {
        margin-bottom: 20px;
    }
}

.sign-up-back-btn {
    position: absolute;
    z-index: 11;
    top: -8px;
    left: 0;

    .svg-icon {
        width: 32px;
        height: 32px;
    }
}
</style>
